/**
 * src/services/spatial/geoLinkCapabilities.ts
 *
 * Client for `GET /api/capabilities`, GeoLink's explicit feature probe.
 *
 * This replaces inferring behaviour from response shape. GeoLink drops
 * unknown query parameters and falls back on unknown `sort` values
 * rather than erroring, so a deployment that does not support
 * server-side filtering answers a filtered request with a plausible but
 * unfiltered page. Guessing from the presence of `total` worked, but
 * GeoLink asked us to build against the real probe instead — see
 * `docs/geolink-organizations-followup.md` §C1 and their §12.
 *
 * Three outcomes, and the difference matters:
 *
 *   - **200** → use the document.
 *   - **404** → a deployment predating the endpoint (Azure today).
 *     Fall back to client-side filtering. This is a definite answer.
 *   - **anything else** → unknown. Do NOT degrade; GeoLink is explicit
 *     that a 503 here means their schema probe failed, not that
 *     filtering is unsupported. We retry on the next call rather than
 *     caching a wrong "no".
 */

import { getGeoLinkApiBaseUrl } from './spatialEnv';

// ─── Document shape ───────────────────────────────────────────────────

export interface GeoLinkAdminListCapabilities {
  total?: boolean;
  sort?: boolean;
  bbox?: boolean;
  near?: boolean;
  search?: boolean;
  /** pg_trgm present. Results are identical either way; this is a perf hint. */
  searchIndexed?: boolean;
}

export interface GeoLinkSpatialCapabilities {
  adminList?: GeoLinkAdminListCapabilities;
  publicListSearch?: boolean;
  countryCode?: boolean;
  organizationBinding?: boolean;
  /** 'guarded' → a bridged id can never fail the object write. */
  metadataOrganizationBridge?: 'guarded' | 'unavailable' | string;
  requirePresence?: boolean;
  executeAcceptsActorCoords?: boolean;
}

export interface GeoLinkOrganizationCapabilities {
  mirror?: boolean;
  scopedWriteCredential?: boolean;
  operators?: boolean;
  reports?: boolean;
  chainIndexer?: boolean;
}

export interface GeoLinkCapabilities {
  service?: string;
  generatedAt?: string;
  capabilities?: {
    spatialObjects?: GeoLinkSpatialCapabilities;
    organizations?: GeoLinkOrganizationCapabilities;
  };
}

/**
 * Three-valued on purpose:
 *   - a document → GeoLink answered;
 *   - `null` → 404, a deployment predating the endpoint. Definite.
 *   - `undefined` → we could not find out. Never cached, never treated
 *     as "unsupported".
 */
export type CapabilitiesResult = GeoLinkCapabilities | null | undefined;

// ─── Cache ────────────────────────────────────────────────────────────

/**
 * GeoLink caches the document for 60s server-side; we hold it for the
 * session. Nothing in it changes without a GeoLink deploy, and a stale
 * "supported" that briefly becomes wrong costs one unfiltered list, not
 * correctness.
 */
let cached: GeoLinkCapabilities | null | undefined;
let resolved = false;
let inFlight: Promise<CapabilitiesResult> | null = null;

/** Test / dev hook — forces the next call to re-probe. */
export function resetGeoLinkCapabilities(): void {
  cached = undefined;
  resolved = false;
  inFlight = null;
}

export async function getGeoLinkCapabilities(): Promise<CapabilitiesResult> {
  if (resolved) return cached;
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<CapabilitiesResult> => {
    const base = getGeoLinkApiBaseUrl().replace(/\/+$/, '');
    try {
      // No auth header: the backend proxy injects the server-side key.
      const res = await fetch(`${base}/api/capabilities`);

      if (res.status === 404) {
        cached = null;
        resolved = true;
        return cached;
      }
      if (!res.ok) {
        // Unknown, not unsupported. Nothing is cached, so the next
        // caller re-probes instead of inheriting a guess.
        return undefined;
      }

      cached = (await res.json()) as GeoLinkCapabilities;
      resolved = true;
      return cached;
    } catch {
      // Network failure is also "unknown".
      return undefined;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

// ─── Derived questions ────────────────────────────────────────────────

/**
 * Whether GeoLink will honour `bbox`, `q` and `country_code` on the
 * admin list. False for old deployments and whenever we could not find
 * out — the client-side pass is the safe answer in both cases.
 */
export async function adminListFiltersServerSide(): Promise<boolean> {
  const caps = await getGeoLinkCapabilities();
  const admin = caps?.capabilities?.spatialObjects?.adminList;
  return !!(admin?.bbox && admin?.search);
}

/**
 * Whether writing `metadata.organization_id` is safe.
 *
 * Only an explicit `guarded`, or an explicit "there is no organization
 * column", earns a yes. Everything else — including a 404 — is a no.
 *
 * A 404 looks like it should be safe, on the reasoning that a
 * deployment without `/api/capabilities` predates migration 042 and so
 * has no column to promote into. That reasoning is wrong, and we have
 * seen it be wrong: a local GeoLink was observed serving `total`,
 * server-side `q` filtering and an `organizationId` field on every row
 * — so migrations 042+ were applied — while `/api/capabilities` still
 * 404'd, because the process had not been restarted onto the build that
 * mounts it. Migrations and code deploy separately, and the window
 * between them is exactly the unsafe one: that build promotes
 * `metadata.organization_id` straight into a foreign key with no
 * fallback, so one unmirrored organization rejects the entire object
 * write, geometry and tiers included.
 *
 * The cost of being wrong in the other direction is a metadata key that
 * does not get written until the deployment is upgraded, which the next
 * save repairs. That is not close to the same price.
 */
export async function metadataOrganizationBridgeIsSafe(): Promise<boolean> {
  const caps = await getGeoLinkCapabilities();
  if (!caps) return false;
  const spatial = caps.capabilities?.spatialObjects;
  if (spatial?.metadataOrganizationBridge === 'guarded') return true;
  return spatial?.organizationBinding === false;
}

/** Whether GeoLink has the organization mirror tables and API. */
export async function organizationMirrorAvailable(): Promise<boolean> {
  const caps = await getGeoLinkCapabilities();
  return !!caps?.capabilities?.organizations?.mirror;
}
